import { createSelector } from 'reselect';
import moment from 'moment';
import {
  find,
  map,
  path,
  pipe,
  prop,
  propOr,
  sortWith,
  descend,
  fromPairs,
  assoc,
  juxt,
  mergeLeft,
  head,
  pathEq,
  applySpec,
  pathOr,
  defaultTo,
  replace,
  propEq,
} from 'ramda';

import { REGISTERED } from 'constants/observationStatuses';

import { NA } from 'constants/common';
import i18n from 'services/i18n';
import { formatDate, formatDateOr } from 'utilities/formatters';
import { DOCTOR } from 'constants/userRoles';

const localState = path(['reports', 'recent']);

export const getReportsById = id =>
  createSelector(
    localState,
    prop(id),
  );

export const getReportsLoadingStatus = id =>
  createSelector(
    getReportsById(id),
    propOr(true, 'loading'),
  );

export const getReportsData = id =>
  createSelector(
    getReportsById(id),
    prop('data'),
  );

export const getReportsShouldShowLoading = id =>
  createSelector(
    [getReportsLoadingStatus(id), getReportsData(id)],
    (loading, data) => loading && !data,
  );

export const getRecentReports = id =>
  createSelector(
    getReportsData(id),
    pipe(
      propOr([], 'entry'),
      map(item => {
        const dateTime = path(['resource', 'effectiveDateTime'])(item);
        return {
          dateTime,
          date: formatDate(i18n.t('dateTimeFormat.reportHistoryDate'))(dateTime),
          id: path(['resource', 'id'], item),
          read: pipe(
            path(['resource', 'component']),
            find(pathEq(['code', 'coding', '0', 'code'], 'opened_by_patient')),
            Boolean,
          )(item),
          status: path(['resource', 'status'], item),
        };
      }),
    ),
  );

export const getSortedRecentReports = id =>
  createSelector(
    getRecentReports(id),
    sortWith([descend(prop('dateTime'))]),
  );

export const getSortedRecentReportsForTechnician = id =>
  createSelector(
    getRecentReports(id),
    map(item =>
      assoc('date', formatDate(i18n.t('dateTimeFormat.reportRecentDate'))(item.dateTime), item),
    ),
  );

export const getSortedRecentReportsWithToday = id =>
  createSelector(
    getSortedRecentReports(id),
    items => {
      const now = moment()
        .startOf('day')
        .valueOf();
      return map(item =>
        !(
          now -
          moment(item.dateTime)
            .startOf('day')
            .valueOf()
        )
          ? assoc(
              'date',
              i18n.t('dateTimeFormat.reportHistoryToday', {
                time: formatDate(i18n.t('dateTimeFormat.reportRecentTime'))(item.dateTime),
              }),
              item,
            )
          : item,
      )(items);
    },
  );

export const mergeAlertsInfoWithPatientName = dependentData => {
  const memoizedPatientList = pipe(
    pipe(
      propOr([], 'entry'),
      map(juxt([path(['resource', 'id']), path(['resource', 'name', '0', 'text'])])),
    ),
    fromPairs,
  )(dependentData);

  return map(({ patientId, ...rest }) =>
    assoc('name', memoizedPatientList[patientId], { patientId, ...rest }),
  );
};

export const handleCVReportsData = pipe(
  propOr([], 'entry'),
  map(
    pipe(
      propOr({}, 'resource'),
      applySpec({
        lastUpdated: pipe(
          path(['meta', 'lastUpdated']),
          lastUpdated => formatDate(i18n.t('dateTimeFormat.lastShockDateTime'))(lastUpdated),
        ),
        reportDate: pipe(
          propOr([], 'extension'),
          head,
          propOr('', 'valueDateTime'),
          valueDateTime => formatDateOr(NA, i18n.t('dateTimeFormat.engagementDate'))(valueDateTime),
        ),
        resourceType: prop('resourceType'),
        type: pipe(
          path(['type', 'coding']),
          head,
          prop('code'),
        ),
        id: prop('id'),
      }),
    ),
  ),
);

export const handleReportsHistoryData = pipe(
  propOr([], 'entry'),
  data => {
    const now = moment()
      .startOf('day')
      .valueOf();

    return map(
      pipe(
        propOr({}, 'resource'),
        applySpec({
          id: prop('id'),
          completedBy: pipe(
            propOr([], 'performer'),
            find(pathEq(['identifier', 'value'], DOCTOR)),
            prop('display'),
            value => (value ? i18n.t('reports.doctorName', { value }) : NA),
          ),
          createDate: pipe(
            prop('effectiveDateTime'),
            createDate =>
              !(
                now -
                moment(createDate)
                  .startOf('day')
                  .valueOf()
              )
                ? i18n.t('dateTimeFormat.reportHistoryToday', {
                    time: formatDate(i18n.t('dateTimeFormat.reportRecentTime'))(createDate),
                  })
                : formatDate(i18n.t('dateTimeFormat.reportHistoryDate'))(createDate),
          ),
          deviceStatus: pipe(
            prop('component'),
            find(pathEq(['code', 'coding', '0', 'code'], 'device_status')),
            prop('valueString'),
          ),
          reviewStatus: prop('status'),
          openedByPatient: pipe(
            propOr([], 'component'),
            find(pathEq(['code', 'coding', '0', 'code'], 'opened_by_patient')),
            prop('valueDateTime'),
            Boolean,
          ),
        }),
      ),
    )(data);
  },
);

export const handleReportsReportsListMergedWithPatientData = ({
  patientsData,
  withNotifications,
}) =>
  pipe(
    propOr([], 'entry'),
    map(
      pipe(
        propOr({}, 'resource'),
        resource => {
          const patientId = pipe(
            path(['subject', 'reference']),
            replace('Patient/', ''),
          )(resource);

          const assigned = pipe(
            propOr([], 'performer'),
            find(pathEq(['identifier', 'value'], DOCTOR)),
            prop('display'),
            value => (value ? i18n.t('reports.doctorName', { value }) : NA),
          )(resource);

          const assignedTo = pipe(
            propOr([], 'component'),
            find(pathEq(['code', 'coding', '0', 'code'], 'reviewed_by')),
            prop('valueString'),
            value => (value ? i18n.t('reports.doctorName', { value }) : assigned),
          );

          return pipe(
            applySpec({
              id: prop('id'),
              patientName: pipe(
                path(['subject', 'display']),
                defaultTo(NA),
              ),
              reviewedBy: pipe(
                propOr([], 'component'),
                find(pathEq(['code', 'coding', '0', 'code'], 'reviewed_by')),
                prop('valueString'),
                value => (value ? i18n.t('reports.doctorName', { value }) : assigned),
              ),
              assignedTo,
              lastUpdated: pipe(
                prop('effectiveDateTime'),
                dateTime => formatDate(i18n.t('dateTimeFormat.reportHistoryDate'))(dateTime),
              ),
              withNotification: pipe(
                propEq('status', REGISTERED),
                status => withNotifications && status,
              ),
            }),
            mergeLeft({
              patientId,
              patientName: pathOr(NA, [patientId, 'name'])(patientsData),
              language: pathOr(NA, [patientId, 'lang'])(patientsData),
            }),
          )(resource);
        },
      ),
    ),
  );
